const DEFAULT_BG = '#14100D';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const parseHex = (hex) => {
  const normalized = hex.replace('#', '');
  if (normalized.length === 3) {
    const [r, g, b] = normalized.split('');
    return {
      r: parseInt(r + r, 16),
      g: parseInt(g + g, 16),
      b: parseInt(b + b, 16),
    };
  }
  if (normalized.length !== 6) return null;
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

export const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((channel) => clamp(Math.round(channel), 0, 255).toString(16).padStart(2, '0'))
    .join('')}`;

export const lerpHex = (from, to, t) => {
  const a = parseHex(from);
  const b = parseHex(to);
  if (!a || !b) return from;
  const ratio = clamp(t, 0, 1);
  return rgbToHex({
    r: a.r + (b.r - a.r) * ratio,
    g: a.g + (b.g - a.g) * ratio,
    b: a.b + (b.b - a.b) * ratio,
  });
};

const colorFromGradient = (el, viewportY) => {
  const raw = el.getAttribute('data-nav-bg-gradient');
  if (!raw) return null;

  const stops = raw.split(',').map((stop) => stop.trim()).filter(Boolean);
  if (!stops.length) return null;
  if (stops.length === 1) return stops[0];

  const rect = el.getBoundingClientRect();
  if (rect.height <= 0) return stops[0];

  const t = clamp((viewportY - rect.top) / rect.height, 0, 1);

  if (stops.length === 2) {
    return lerpHex(stops[0], stops[1], t);
  }

  const [from, via, to] = stops;
  if (t <= 0.5) return lerpHex(from, via, t * 2);
  return lerpHex(via, to, (t - 0.5) * 2);
};

const colorFromSection = (section, sampleY) => {
  if (section.hasAttribute('data-nav-bg-gradient')) {
    return colorFromGradient(section, sampleY);
  }
  return section.getAttribute('data-nav-bg');
};

/** Prefer the deepest / smallest zone that contains the header sample line. */
const pickSectionAtY = (sampleY) => {
  const zones = [
    ...document.querySelectorAll('[data-nav-bg], [data-nav-bg-gradient]:not([data-nav-bg-fallback])'),
  ];

  const matches = zones.filter((el) => {
    const rect = el.getBoundingClientRect();
    return rect.top <= sampleY && rect.bottom >= sampleY;
  });

  if (!matches.length) return null;

  return matches.reduce((best, el) => {
    if (best.contains(el)) return el;
    if (el.contains(best)) return best;

    const bestRect = best.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    return elRect.height <= bestRect.height ? el : best;
  });
};

export const resolveNavBackgroundColor = (headerSelector = '[data-site-header]') => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return DEFAULT_BG;
  }

  const header = document.querySelector(headerSelector);
  const sampleY = Math.max(1, Math.floor((header?.offsetHeight ?? 56) / 2));

  const activeSection = pickSectionAtY(sampleY);
  if (activeSection) {
    const color = colorFromSection(activeSection, sampleY);
    if (color) return color;
  }

  const fallback = document.querySelector('[data-nav-bg-fallback]');
  if (fallback) {
    const color = colorFromSection(fallback, sampleY);
    if (color) return color;
  }

  return DEFAULT_BG;
};
